import type { ReactElement } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { PixelPlaceholderLoader } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { useCurrentPageState } from "app/components/educationbar/education-utils";
import { DemoDisabledTooltip } from "app/pages/sales-intelligence/common-components/demo-mode/DemoDisabledTooltip";
import {
    StyledLeadsPageActionsWrapper,
    StyledLeadsPageHeaderWrapper,
} from "app/pages/sales-intelligence/common-components/layout";
import LoaderWithProcessStatus from "app/pages/sales-intelligence/common-components/LoaderWithProcessStatus/LoaderWithProcessStatus";
import SavedSearchActionsContainer from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/containers/SavedSearchActionsContainer";
import SavedSearchButtonContainer from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/containers/SavedSearchButtonContainer";
import SavedSearchPanelContainer from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/containers/SavedSearchPanelContainer";
import { useUpdateSavedSearchMutationState } from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/mutations/useUpdateSavedSearchMutationState";
import { useSingleSavedSearch } from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/query";
import type { SavedSearchRoutingParams } from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/types";
import DataCreditsIndicatorContainer from "app/pages/sales-intelligence/modules/quota/containers/DataCreditsIndicatorContainer/DataCreditsIndicatorContainer";
import advancedSearchPageStorage from "app/pages/sales-intelligence/pages/advanced-search/storage";
import { useIsDemoMode } from "app/pages/sales-intelligence/store/demo-mode/useDemoModeStore";
import { SaveSearchAICreated } from "app/pages/sales-intelligence/sub-modules/advanced-search/types/dto";
import { EducationBarIconButton, useEducationBarState } from "app/pages/sales-intelligence/sub-modules/education-bar";
import EducationBarContainer from "app/pages/sales-intelligence/sub-modules/education-bar/containers/EducationBarContainer";

import PageTitle from "../../components/PageTitle/PageTitle";

import { StyledHeaderTitleContainer } from "./styles";

const PageHeader = (): ReactElement => {
    const {
        params: { savedSearchId },
    } = useCurrentStateAndParams<SavedSearchRoutingParams>();

    const stateId = useCurrentPageState();
    const isDemoMode = useIsDemoMode();
    const { isBarVisible, onClose, onOpen } = useEducationBarState(advancedSearchPageStorage);

    const { data: savedSearchData, isLoading: isSavedSearchLoading } = useSingleSavedSearch(savedSearchId);

    const updateSavedSearchMutationState = useUpdateSavedSearchMutationState();

    const isAICreated = savedSearchData?.createdBy === SaveSearchAICreated;

    const subTitle = isSavedSearchLoading ? (
        <PixelPlaceholderLoader
            width={150}
            height={14}
        />
    ) : !isAICreated ? (
        savedSearchData?.name
    ) : undefined;

    return (
        <>
            {isBarVisible ? (
                <EducationBarContainer
                    stateId={stateId || ""}
                    onClose={onClose}
                />
            ) : null}

            <StyledLeadsPageHeaderWrapper>
                <StyledHeaderTitleContainer>
                    <PageTitle subTitle={subTitle} />

                    <LoaderWithProcessStatus
                        isLoading={updateSavedSearchMutationState?.status === "pending"}
                        error={updateSavedSearchMutationState?.error?.message}
                    />
                </StyledHeaderTitleContainer>

                <Flex
                    gap="8px"
                    alignItems="center"
                >
                    <DataCreditsIndicatorContainer />

                    <SavedSearchPanelContainer />

                    {savedSearchId ? (
                        <SavedSearchActionsContainer isUpdateEnabled={!isAICreated && !isDemoMode} />
                    ) : (
                        <DemoDisabledTooltip>
                            <SavedSearchButtonContainer />
                        </DemoDisabledTooltip>
                    )}

                    {isBarVisible ? null : (
                        <StyledLeadsPageActionsWrapper>
                            <EducationBarIconButton onClick={onOpen} />
                        </StyledLeadsPageActionsWrapper>
                    )}
                </Flex>
            </StyledLeadsPageHeaderWrapper>
        </>
    );
};

export default PageHeader;
